import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["analyticsChannel", "remotePlugin", "exportName", "children", "Fallback"],
  _excluded2 = ["analyticsChannel", "remotePlugin", "ErrorFallback", "onEvent"];
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import '../../services/remote-setup';
import ErrorBoundary from '../../common/ui/error-boundary';
import { NO_OP } from '../../common/utils';
import useRemoteModule from '../../services/use-remote-module';
var InternalRemoteComponent = function InternalRemoteComponent(_ref) {
  var analyticsChannel = _ref.analyticsChannel,
    remotePlugin = _ref.remotePlugin,
    _ref$exportName = _ref.exportName,
    exportName = _ref$exportName === void 0 ? 'default' : _ref$exportName,
    _ref$children = _ref.children,
    children = _ref$children === void 0 ? null : _ref$children,
    _ref$Fallback = _ref.Fallback,
    Fallback = _ref$Fallback === void 0 ? null : _ref$Fallback,
    componentProps = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    setError = _useState2[1];
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  useEffect(function () {
    var analyticsEvent = createAnalyticsEvent({
      action: 'rendered',
      actionSubject: 'remoteModule',
      actionSubjectId: 'remoteComponent',
      attributes: {
        exportName: exportName,
        dimensions: remotePlugin.dimensions,
        plugin: remotePlugin.constructor.name
      }
    });
    analyticsEvent.fire(analyticsChannel);
  }, [analyticsChannel, createAnalyticsEvent, exportName, remotePlugin]);
  var onError = useCallback(function (error) {
    // this is a workaround to throw an error from an async function
    setError(function () {
      throw error;
    });
  }, []);
  var remoteFrontendSdk = useRemoteModule(remotePlugin, {
    analyticsChannel: analyticsChannel,
    onError: onError,
    onEvent: componentProps.onEvent
  });
  useEffect(function () {
    return function () {
      void remoteFrontendSdk.renderUfoExperience.abort({
        metadata: {
          moduleName: remoteFrontendSdk.getModuleName()
        }
      });
    };
  }, [remoteFrontendSdk]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, remoteFrontendSdk.renderComponent(exportName, componentProps, Fallback, children, remoteFrontendSdk.renderUfoExperience, componentProps.onEvent));
};
var RemoteComponent = function RemoteComponent(_ref2) {
  var analyticsChannel = _ref2.analyticsChannel,
    remotePlugin = _ref2.remotePlugin,
    _ref2$ErrorFallback = _ref2.ErrorFallback,
    ErrorFallback = _ref2$ErrorFallback === void 0 ? null : _ref2$ErrorFallback,
    _ref2$onEvent = _ref2.onEvent,
    onEvent = _ref2$onEvent === void 0 ? NO_OP : _ref2$onEvent,
    props = _objectWithoutProperties(_ref2, _excluded2);
  useEffect(function () {
    return function () {
      onEvent({
        type: 'unmount'
      });
    };
  }, [onEvent]);
  var onError = useCallback(function (error) {
    onEvent({
      error: error,
      type: 'render-error'
    });
  }, [onEvent]);
  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    analyticsChannel: analyticsChannel,
    remotePlugin: remotePlugin,
    onError: onError,
    fallback: ErrorFallback
  }, /*#__PURE__*/React.createElement(InternalRemoteComponent, _extends({
    analyticsChannel: analyticsChannel,
    remotePlugin: remotePlugin,
    onEvent: onEvent
  }, props)));
};
export default /*#__PURE__*/memo(RemoteComponent);